import { Injectable, NgZone } from '@angular/core';
import { Firestore, collection, collectionData, doc, setDoc, deleteDoc, DocumentReference, CollectionReference, addDoc, getDoc, getDocs, query, orderBy, limit, startAfter, docData, DocumentChange, onSnapshot, Timestamp, docSnapshots, QueryConstraint, QueryDocumentSnapshot } from '@angular/fire/firestore';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { Member } from '../models/member.model';
import { DataService } from './data';
import { Auth, authState, User } from '@angular/fire/auth';
import { Observable, of, EMPTY, from } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Task } from '../models/task.model';
import { Periodo } from '../models/periodo.model';
import { buildPeriodosFinancierosState, PeriodosFinancierosState, EMPTY_PERIODOS_FINANCIEROS_STATE } from '../utils/periodo.util';
import { PreInformeDoc, PreInformeItem } from '../models/preinforme.model';
import { Iglesia } from '../models/iglesia.model';
import { updatePassword } from 'firebase/auth';

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {
  private usersObservationSubscription: (() => void) | undefined;

  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private dataService: DataService,
    private zone: NgZone
  ) { }

  /**
   * Página de registros eliminados (tombstones) de una iglesia.
   * Lectura paginada con getDocs (sin listener) para no consumir cuota innecesaria.
   * Ruta: iglesias/{churchId}/eliminados, ordenado por eliminadoEn desc.
   */
  async getEliminadosPage(
    churchId: string,
    pageSize = 25,
    startAfterDoc?: QueryDocumentSnapshot | null
  ): Promise<{ items: any[]; lastDoc: QueryDocumentSnapshot | null }> {
    if (!churchId) return { items: [], lastDoc: null };

    const colRef = collection(this.firestore, `iglesias/${churchId}/eliminados`);
    const constraints: QueryConstraint[] = [orderBy('eliminadoEn', 'desc')];
    if (startAfterDoc) {
      constraints.push(startAfter(startAfterDoc));
    }
    constraints.push(limit(pageSize));

    const snap = await getDocs(query(colRef, ...constraints));
    const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
    const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { items, lastDoc };
  }

  /** Guarda la firma digital en Storage y Firestore, y actualiza DataService */
  async saveChurchSignature(churchId: string, base64: string): Promise<string | null> {
    if (!churchId || !base64) return null;
    try {
      // Subir a Storage
      const storage = getStorage();
      const rutaRef = ref(storage, `iglesias/${churchId}/firma`);
      await uploadString(rutaRef, base64, 'data_url');
      const url = await getDownloadURL(rutaRef);

      // Guardar en Firestore
      const docRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/firma`);
      await setDoc(docRef, { firmaUrl: url }, { merge: true });

      // Actualizar DataService para difusión reactiva
      this.dataService.updateChurchSignatureUrl(url);
      return url;
    } catch (e) {
      return null;
    }
  }

  /** Observa la firma digital y actualiza DataService */
  observeChurchSignature(churchId: string): Observable<string | null> {
    if (!churchId) return of(null);
    const docRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/firma`);
    return docData(docRef).pipe(
      map((data: any) => data?.firmaUrl ?? null),
      tap((url: string | null) => {
        this.dataService.updateChurchSignatureUrl(url);
      }),
      catchError((err) => {
        this.dataService.updateChurchSignatureUrl(null);
        return of(null);
      })
    );
  }

  loadAndObserveUsers(churchId: string): void {
    // Stop previous observation if exists before starting a new one
    if (this.usersObservationSubscription) {
      this.usersObservationSubscription();
      this.usersObservationSubscription = undefined;
    }

    if (!churchId) {
      this.dataService.updateUsers([]);
      this.dataService.setAppUsersObserving(false);
      return;
    }

    const usersCollectionRef = collection(this.firestore, `iglesias/${churchId}/usuarios`);
    this.usersObservationSubscription = onSnapshot(usersCollectionRef,
      (querySnapshot) => {
        this.zone.run(() => {
          const users = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Member));
          this.dataService.updateUsers(users);
          this.dataService.setAppUsersObserving(true);
        });
      },
      (error) => {
        this.zone.run(() => {
          this.dataService.setAppUsersObserving(false);
          this.dataService.updateUsers([]);
        });
      }
    );
  }

  stopObservingUsers(): void {
    if (this.usersObservationSubscription) {
      this.usersObservationSubscription();
      this.usersObservationSubscription = undefined;
    }
  }

  getAuthState(): Observable<User | null> {
    return authState(this.auth);
  }

  private getTasksCollectionRef(churchId: string): CollectionReference<Task> {
    if (!churchId) {
      throw new Error('Church ID is required to get tasks collection reference.');
    }
    return collection(this.firestore, `iglesias/${churchId}/tareas`) as CollectionReference<Task>;
  }

  getTasks(churchId: string): Observable<Task[]> {
    if (!churchId) {
      return of([]);
    }
    const tasksCollection = this.getTasksCollectionRef(churchId);
    return collectionData(tasksCollection, { idField: 'id' }) as Observable<Task[]>;
  }

  getMembers(churchId: string): Observable<Member[]> {
    if (!churchId) {
      return of([]);
    }
    return collectionData(collection(this.firestore, 'iglesias', churchId, 'miembros'), { idField: 'id' }) as Observable<Member[]>;
  }

  getMemberById(churchId: string, memberId: string): Observable<Member | undefined> {
    if (!churchId) {
      return of(undefined);
    }
    const docRef = doc(this.firestore, 'iglesias', churchId, 'miembros', memberId);
    return docData(docRef, { idField: 'id' }) as Observable<Member | undefined>;
  }

  /*   getMovimientos(churchId: string, year: string, month: string): Observable<any[]> {
      if (!churchId || !year || !month) {
        return of([]);
      }
      const offeringsCollection = collection(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`);
      const movimientosQuery = query(offeringsCollection, orderBy('fecha', 'desc'));
      return collectionData(movimientosQuery, { idField: 'id' }).pipe(
        tap(movimientos => {
          let totalOfrendas = 0, totalDiezmos = 0, totalSalidas = 0, totalEntradas = 0;
          movimientos.forEach(movimiento => {
            if (movimiento['tipoMovimiento'] === 'Ingreso') {
              totalEntradas += movimiento['monto'] || 0;
              if (movimiento['concepto'] === 'Ofrendas Diarias') totalOfrendas += movimiento['monto'] || 0;
              else if (movimiento['concepto'] === 'Diezmo Iglesia') totalDiezmos += movimiento['monto'] || 0;
            } else if (movimiento['tipoMovimiento'] === 'Egreso') {
              totalSalidas += movimiento['monto'] || 0;
            }
          });
          this.dataService.updateFinancialTotals({ totalOfrendas, totalDiezmos, totalSalidas, totalEntradas, totalDisponible: totalEntradas - totalSalidas });
        })
      );
    } */

  getMovimientos(churchId: string, year: string, month: string): Observable<any[]> {
    if (!churchId || !year || !month) {
      return of([]);
    }

    const offeringsCollection = collection(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`);
    const movimientosQuery = query(offeringsCollection, orderBy('fecha', 'desc'));

    const movimientos$ = collectionData(movimientosQuery, { idField: 'id' }).pipe(
      map((movs: any[]) => {
        return movs.sort((a, b) => {
          // 1. Fecha Desc
          const fA = a.fecha?.toDate ? a.fecha.toDate().getTime() : (a.fecha instanceof Date ? a.fecha.getTime() : 0);
          const fB = b.fecha?.toDate ? b.fecha.toDate().getTime() : (b.fecha instanceof Date ? b.fecha.getTime() : 0);
          if (fB !== fA) return fB - fA;

          // 2. CreatedAt Desc
          const cA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const cB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return cB - cA;
        });
      }),
      tap(movimientos => {
        let totalOfrendas = 0, totalDiezmos = 0, totalSalidas = 0, totalEntradas = 0;
        movimientos.forEach(movimiento => {
          if (movimiento['tipoMovimiento'] === 'Ingreso') {
            totalEntradas += movimiento['monto'] || 0;
            if (movimiento['concepto'] === 'Ofrendas Diarias') totalOfrendas += movimiento['monto'] || 0;
            else if (movimiento['concepto'] === 'Diezmo Iglesia') totalDiezmos += movimiento['monto'] || 0;
          } else if (movimiento['tipoMovimiento'] === 'Egreso') {
            totalSalidas += movimiento['monto'] || 0;
          }
        });
        this.dataService.updateFinancialTotals({
          totalOfrendas,
          totalDiezmos,
          totalSalidas,
          totalEntradas,
          totalDisponible: totalEntradas - totalSalidas
        });
      })
    );

    // comprobar en background si el periodo está cerrado (formato de id: YYYY-MM)
    (async () => {
      try {
        const ym = `${year}-${month.padStart(2, '0')}`;
        const periodoRef = doc(this.firestore, `iglesias/${churchId}/periodos/${ym}`);
        const snap = await getDoc(periodoRef);
        if (snap.exists()) {
          const data = snap.data();
          // esperar que el campo sea 'cerrado' booleano; soportar fallback a 'closed' o 'status'
          const cerrado = !!data['closed'];
          this.dataService.updatePeriodoCerrado(cerrado);
        }
      } catch (err) {
      }
    })();

    return movimientos$;
  }

  getMovimientosPrevYM(churchId: string, year: string, month: string): Observable<any[]> {
    if (!churchId || !year || !month) {
      return of([]);
    }
    const offeringsCollection = collection(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`);
    const movimientosQuery = query(offeringsCollection, orderBy('fecha', 'desc'));
    return collectionData(movimientosQuery, { idField: 'id' }).pipe(
      tap(movimientos => {
        let totalOfrendas = 0, totalDiezmos = 0, totalSalidas = 0, totalEntradas = 0;
        movimientos.forEach(movimiento => {
          if (movimiento['tipoMovimiento'] === 'Entrada') {
            totalEntradas += movimiento['monto'] || 0;
            if (movimiento['tipo'] === 'Ofrenda') totalOfrendas += movimiento['monto'] || 0;
            else if (movimiento['tipo'] === 'Diezmo') totalDiezmos += movimiento['monto'] || 0;
          } else if (movimiento['tipoMovimiento'] === 'Salida') {
            totalSalidas += movimiento['monto'] || 0;
          }
        });
        this.dataService.updateFinancialTotals({ totalOfrendas, totalDiezmos, totalSalidas, totalEntradas, totalDisponible: totalEntradas - totalSalidas });
      })
    );
  }

  getPreInforme(churchId: string): Observable<PreInformeDoc | null> {
    if (!churchId) return of(null);

    // Ruta: iglesias/{churchId}/configuraciones/preInforme (doc)
    const rutaCompleta = `iglesias/${churchId}/configuraciones/preInforme`;
    const preInformeRef = doc(this.firestore, rutaCompleta);

    return docData(preInformeRef, { idField: 'id' }).pipe(
      map((data) => {
        return data ? (data as PreInformeDoc) : null;
      }),
      tap((preInforme) => {
        this.dataService.updatePreInforme(preInforme);
      }),
      catchError((err) => {
        this.dataService.updatePreInforme(null);
        return of(null);
      })
    );
  }

  async addMovimiento(churchId: string, movimientoData: any): Promise<DocumentReference<any>> {
    if (!churchId) throw new Error('Cannot add offering: Church ID is not available.');
    const date = movimientoData.fecha instanceof Date ? movimientoData.fecha : movimientoData.fecha.toDate();
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const movementsCollection = collection(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`);
    return await addDoc(movementsCollection, movimientoData);
  }

  async updateMovimiento(churchId: string, movimientoData: any): Promise<void> {
    if (!churchId || !movimientoData?.id) throw new Error('Church ID and movement data with ID are required.');
    const date = movimientoData.fecha.toDate ? movimientoData.fecha.toDate() : new Date(movimientoData.fecha);
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const docRef = doc(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`, movimientoData.id);
    await setDoc(docRef, movimientoData, { merge: true });
  }

  async deleteMovimiento(churchId: string, movimientoData: any): Promise<void> {
    if (!churchId || !movimientoData?.id) throw new Error('Church ID and movement data with ID are required.');
    const date = movimientoData.fecha.toDate ? movimientoData.fecha.toDate() : new Date(movimientoData.fecha);
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const docRef = doc(this.firestore, `iglesias/${churchId}/movimientos/${year}/${month}`, movimientoData.id);
    await deleteDoc(docRef);
  }

  async getPreviousMonthSummary(churchId: string, year: number, month: number): Promise<any | null> {
    if (!churchId) return null;
    const docPath = `iglesias/${churchId}/resumenMensual/${year}/${month.toString().padStart(2, '0')}/resumen`;
    const summaryDoc = await getDoc(doc(this.firestore, docPath));
    if (summaryDoc.exists()) {
      this.dataService.updatePreviousMonthSummary(summaryDoc.data());
      return summaryDoc.data();
    }
    return null;
  }

  getIglesia(churchId: string): Observable<Iglesia | null> {
    if (!churchId) return of(null);
    const ref = doc(this.firestore, `iglesias/${churchId}`);
    return docSnapshots(ref).pipe(
      map(snap => snap.exists()
        ? ({ id: snap.id, ...(snap.data() as any) } as Iglesia)
        : null),
      catchError(err => {
        return of(null);
      })
    );
  }

  getDocument(path: string, docId: string) {
    const ref = doc(this.firestore, `${path}/${docId}`);
    return docData(ref);
  }

  setDocument(path: string, docId: string, data: any) {
    const ref = doc(this.firestore, `${path}/${docId}`);
    return setDoc(ref, data);
  }

  getDocumentRealtime<T>(collectionPath: string, docId: string): Observable<T | null> {
    const ref = doc(this.firestore, collectionPath, docId);
    return docSnapshots(ref) as Observable<T | null>;
  }

  getPeriodo(churchId: string, ym: string): Observable<Periodo | null> {
    const ref = doc(this.firestore, `iglesias/${churchId}/periodos/${ym}`);
    return docSnapshots(ref).pipe(
      map(snap => snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as Periodo) : null),
      catchError(() => of(null))
    );
  }

  getPeriodosFinancierosState(churchId: string): Observable<PeriodosFinancierosState> {
    if (!churchId) return of(EMPTY_PERIODOS_FINANCIEROS_STATE);
    const colRef = collection(this.firestore, `iglesias/${churchId}/periodos`);
    return collectionData(colRef, { idField: 'id' }).pipe(
      map((docs: any[]) => buildPeriodosFinancierosState(docs)),
      catchError(() => of(EMPTY_PERIODOS_FINANCIEROS_STATE))
    );
  }

  getOldestOpenPeriodo(churchId: string): Observable<Periodo | null> {
    return this.getPeriodosFinancierosState(churchId).pipe(
      map((state) => {
        if (!state.oldestYm) return null;
        return { id: state.oldestYm, ym: state.oldestYm, status: 'abierto' as const };
      }),
      catchError(() => of(null))
    );
  }

  getCurrentAndPreviousYM(timeZone: string = 'America/Bogota') {
    const now = new Date();

    const pad2 = (n: number | string) => String(n).padStart(2, '0');

    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit'
    });

    // Año y mes actual en la zona
    const parts = fmt.formatToParts(now);
    const currYearStr = parts.find(p => p.type === 'year')!.value;
    const currMonthStr = parts.find(p => p.type === 'month')!.value;

    const currYear = parseInt(currYearStr, 10);
    const currMonth = parseInt(currMonthStr, 10);

    // Calcular el anterior por aritmética
    const prevMonthNum = currMonth === 1 ? 12 : currMonth - 1;
    const prevYearNum = currMonth === 1 ? currYear - 1 : currYear;

    const currYM = `${currYearStr}-${currMonthStr}`;
    const prevYM = `${String(prevYearNum)}-${pad2(prevMonthNum)}`;

    return { currYM, prevYM };
  }

  /**
   * Cambia la contraseña del usuario autenticado en Firebase Auth
   * @param currentPassword La contraseña actual (no se usa en Firebase, pero puede usarse para verificación extra)
   * @param newPassword La nueva contraseña
   * @returns Promise<boolean> true si se cambió correctamente, false si hubo error
   */
  async changeUserPassword(newPassword: string): Promise<boolean> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('No hay usuario autenticado');
      await updatePassword(user, newPassword);
      return true;
    } catch (error: any) {
      throw error;
    }
  }
}
